import { useEffect, useRef, useState } from 'react';
import type { IJourney } from '../../../../models/journey.model';
import './style.scss';
import Details from './components/Details';
import JourneyMap from './components/JourneyMap';
import Prices from './components/Prices';
import useFetch, { DataSource, Status } from '../../../../hooks/fetch.hook';
import { JourneyDetailsApiModel } from '../../../../services/api-planera-resa/models/journey-details/journey-details.api-model';
import type { IJourneyDetails } from '../../../../models/journey-details.model';
import type { IServiceJourney } from '../../../../models/service-journey.model';
import { ISelectedNonPublicTransportLink } from './models/selected-non-public-transport';
import LogService from '../../../../services/log-service/log.service';

function processReceivedData(data: JourneyDetailsApiModel | undefined, journey: IJourney): IJourneyDetails | undefined {
  if (!data) return undefined;

  try {
    const mappedData = JourneyDetailsApiModel.mapToBusinessModel(data);

    return {
      ...mappedData,
      departureAccessLink: mappedData.departureAccessLink,
      arrivalAccessLink: journey.tripLegs && journey.tripLegs.length === 0 ? journey.arrivalAccessLink : mappedData.arrivalAccessLink,
      connectionLinks: mappedData.connectionLinks,
      tripLegs: mappedData.tripLegs,
    };
  } catch (error) {
    LogService.logError(error, 'Error mapping JourneyDetailsApiModel to business model in JourneyDetails');
    return undefined;
  }
}

function JourneyDetails({ journey, isVisible }: { journey: IJourney; isVisible?: boolean }) {
  const journeyMapRef = useRef<HTMLDivElement>(null);
  const [selectedServiceJourney, setSelectedServiceJourney] = useState<IServiceJourney | undefined>(undefined);
  const [selectedNonPublicTransportLink, setSelectedNonPublicTransportLink] = useState<ISelectedNonPublicTransportLink | undefined>(undefined);
  const [currentJourney, setCurrentJourney] = useState<IJourney>(journey);

  useEffect(() => {
    if (!isVisible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedServiceJourney(undefined);
      setSelectedNonPublicTransportLink(undefined);
      setCurrentJourney(journey);
    }
  }, [isVisible, journey]);

  const url = isVisible
    ? `/journeys/${journey.detailsReference}/details?includes=links&includes=ServiceJourneyCalls&includes=TripLegCoordinates&includes=ServiceJourneyCoordinates&includes=ticketsuggestions`
    : undefined;

  const [getJourneyDetails] = useFetch<JourneyDetailsApiModel | undefined>(url, !isVisible, false, {
    dataSource: DataSource.apiPlaneraResa,
    keepLastStateIfPrevented: false,
    delayInMs: 0,
  });

  const journeyDetails = processReceivedData(getJourneyDetails.data, journey);

  return (
    <div className="row travel-planner-details__column">
      {journeyDetails && currentJourney && isVisible && (
        <>
          <div className="col-12 col-md-6 order-2 w-100 mb-3 mb-md-0 travel-planner-details__column-item travel-planner-details__column-item--details">
            <Details
              journey={currentJourney}
              journeyDetails={journeyDetails}
              journeyMapRef={journeyMapRef}
              onServiceJourneySelected={(serviceJourney) => setSelectedServiceJourney(serviceJourney)}
              onNonPublicTransportLinkSelected={(selectedNonPublicTransportLink) => {
                setSelectedNonPublicTransportLink(selectedNonPublicTransportLink);
              }}
            />
          </div>
          <div ref={journeyMapRef} className="col-12 col-md-6 travel-planner-details__column-break order-1 w-100 mb-3 travel-planner-details__column-item travel-planner-details__column-item--map">
            <JourneyMap
              journey={journey}
              journeyDetails={journeyDetails}
              selectedServiceJourney={selectedServiceJourney}
              selectedNonPublicTransportLink={selectedNonPublicTransportLink}
              isLoading={getJourneyDetails.status === Status.loading}
            />
          </div>
          <div className="col-12 col-md-6 order-3 w-100 travel-planner-details__column-item travel-planner-details__column-item--prices">
            <Prices journey={currentJourney} journeyDetails={journeyDetails} />
          </div>
        </>
      )}
    </div>
  );
}

export default JourneyDetails;
